import { useState, type ReactNode } from "react";
import { format, differenceInDays } from "date-fns";
import { Clock, Activity, ChevronDown, MapPin, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EvalStatusBadge } from "@/components/animals/EvalStatusBadge";
import { formatReportDate } from "@/lib/utils";
import {
  FieldChange,
  getTimelineChangeLabel,
  deduplicateStatusChanges,
  LOCATION_LABELS,
  buildStatusSummary,
} from "@/lib/timeline-utils";
import { getStayColor } from "@/lib/stay-colors";
import { KennelStatusStrip, type HistoryEntry, type StatusHistory } from "./StatusHistoryCard";

export interface TimelineSnapshot {
  reportDate: string;
  location: string;
  zoneCode: string | null;
  kennelStatusNorm: string | null;
  substatusNorm: string | null;
  statusCode: string | null;
  adoptionLevelNorm: string | null;
  intakeDate: string | null;
  animalEvalStatus: string | null;
  animalEvalRaw: string | null;
  changesFromPrevious: FieldChange[];
}

export interface Stay {
  stayIndex: number;
  location?: string;
  startDate: string;
  endDate: string;
  outcomedInReports: boolean;
  outcomeDate?: string | null;
  outcomeReason?: string | null;
  admissionGroupId?: number;
  linkedToPriorStay?: boolean;
  snapshots: TimelineSnapshot[];
}

export interface Timeline {
  stays: Stay[];
}

interface AnimalTimelineCardProps {
  timeline: Timeline | null;
  animalId?: string;
  onOutcomeReasonChange?: (reason: string, stayIndex: number) => void;
  isOutcomeReasonPending?: boolean;
  localOutcomeReasons?: Record<number, string>;
}

function segmentsForStay(
  segments: HistoryEntry[],
  stayStart: string,
  stayEnd: string
): HistoryEntry[] {
  const todayStr = new Date().toISOString().split('T')[0];
  return segments
    .filter((seg) => {
      const segEnd = seg.endDate ?? "9999-12-31";
      return seg.startDate <= stayEnd && segEnd >= stayStart;
    })
    .map((seg) => {
      const clippedStart = seg.startDate > stayStart ? seg.startDate : stayStart;
      const effectiveEnd = seg.endDate ?? todayStr;
      const clippedEnd = effectiveEnd < stayEnd ? effectiveEnd : stayEnd;
      const clippedDays = Math.max(0, differenceInDays(
        new Date(clippedEnd + "T00:00:00"),
        new Date(clippedStart + "T00:00:00"),
      ));
      return { ...seg, durationDays: clippedDays };
    });
}

const TRUNCATED_SNAPSHOT_COUNT = 7;

function StaySection({
  stay,
  showChanges,
  kennelSegments,
  onOutcomeReasonChange,
  isOutcomeReasonPending,
  localOutcomeReasons,
  inTransferGroup,
}: {
  stay: Stay;
  showChanges: boolean;
  kennelSegments: HistoryEntry[];
  onOutcomeReasonChange?: (reason: string, stayIndex: number) => void;
  isOutcomeReasonPending: boolean;
  localOutcomeReasons: Record<number, string>;
  inTransferGroup?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const intakeDate = stay.snapshots[0]?.intakeDate;
  const displayReason = localOutcomeReasons[stay.stayIndex] ?? stay.outcomeReason;
  const reversedSnapshots = [...stay.snapshots].reverse();
  const needsTruncation = reversedSnapshots.length > TRUNCATED_SNAPSHOT_COUNT;
  const visibleSnapshots = needsTruncation && !expanded
    ? reversedSnapshots.slice(0, TRUNCATED_SNAPSHOT_COUNT)
    : reversedSnapshots;
  const hiddenCount = reversedSnapshots.length - TRUNCATED_SNAPSHOT_COUNT;

  const locationLabel = stay.location
    ? (LOCATION_LABELS[stay.location] ?? stay.location)
    : (stay.snapshots[stay.snapshots.length - 1]?.location
        ? (LOCATION_LABELS[stay.snapshots[stay.snapshots.length - 1].location] ?? stay.snapshots[stay.snapshots.length - 1].location)
        : null);

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant="outline"
          className="font-semibold"
          style={{
            borderColor: getStayColor(stay.stayIndex),
            color: getStayColor(stay.stayIndex),
            backgroundColor: `color-mix(in srgb, ${getStayColor(stay.stayIndex)} 10%, transparent)`,
          }}
        >
          {inTransferGroup
            ? (locationLabel ?? `Stay #${stay.stayIndex}`)
            : `Stay #${stay.stayIndex}${locationLabel ? ` · ${locationLabel}` : ""}`}
        </Badge>
        {(stay.outcomeDate || stay.outcomedInReports) && !(inTransferGroup && stay.outcomeReason === "transferred") && (
          <Badge
            variant="secondary"
            className="text-xs"
            data-testid={`stay-outcomed-badge-${stay.stayIndex}`}
          >
            {displayReason && displayReason !== "unknown"
              ? `Outcomed - ${displayReason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}`
              : "Outcomed"}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
        <span className="text-xs text-muted-foreground py-0.5">Dates</span>
        <span className="text-xs font-medium py-0.5" data-testid={`stay-intake-${stay.stayIndex}`}>
          {format(new Date(intakeDate ?? stay.startDate), "EEE, MMM d")} – {format(new Date(stay.endDate), "EEE, MMM d, yyyy")}
        </span>

        {intakeDate && (
          <>

            <span className="text-xs text-muted-foreground py-0.5">LOS</span>
            <span
              className="text-xs font-medium py-0.5"
              data-testid={`stay-los-${stay.stayIndex}`}
            >
              {(() => {
                const start = new Date(intakeDate);
                const end = stay.outcomeDate
                  ? new Date(stay.outcomeDate + "T00:00:00")
                  : new Date();
                const days = differenceInDays(end, start);
                return `${days} day${days !== 1 ? "s" : ""}${!stay.outcomeDate && !stay.outcomedInReports ? " (ongoing)" : ""}`;
              })()}
            </span>
          </>
        )}

        {(stay.outcomeDate || stay.outcomedInReports) && (
          <>
            <span className="text-xs text-muted-foreground py-0.5 self-center">Outcome</span>
            <div
              className="flex items-center gap-2 flex-wrap py-0.5"
              data-testid={`stay-outcomed-${stay.stayIndex}`}
            >
              <span className="text-xs font-medium">
                {stay.outcomeDate
                  ? format(new Date(stay.outcomeDate + "T00:00:00"), "EEE, MMM d, yyyy")
                  : "Not in later reports"}
              </span>
              {onOutcomeReasonChange && (
                <Select
                  value={localOutcomeReasons[stay.stayIndex] ?? stay.outcomeReason ?? "unknown"}
                  onValueChange={(value) => onOutcomeReasonChange(value, stay.stayIndex)}
                  disabled={isOutcomeReasonPending}
                >
                  <SelectTrigger
                    className="w-[140px] h-7 text-xs"
                    data-testid={`stay-outcome-reason-select-${stay.stayIndex}`}
                  >
                    <SelectValue placeholder="Set reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown">Unknown</SelectItem>
                    <SelectItem value="adopted">Adopted</SelectItem>
                    <SelectItem value="transferred">Transferred</SelectItem>
                    <SelectItem value="returned_to_owner">Returned to Owner</SelectItem>
                    <SelectItem value="foster">Foster</SelectItem>
                    <SelectItem value="euthanized">Euthanized</SelectItem>
                    <SelectItem value="died_in_care">Died in Care</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </>
        )}
      </div>

      <div className="space-y-1.5" data-testid={`kennel-status-history-stay-${stay.stayIndex}`}>
        <p className="text-xs text-muted-foreground">Kennel Status History</p>
        <KennelStatusStrip segments={kennelSegments} />
      </div>

      <div className="flex items-center gap-2">
        <p className="text-xs text-muted-foreground">
          Snapshot History ({stay.snapshots.length})
        </p>
      </div>

      <div className="space-y-2 border-l-2 pl-3" style={{ borderColor: getStayColor(stay.stayIndex) }}>
        {visibleSnapshots.map((snapshot, idx) => {
          const statusSummary = buildStatusSummary(snapshot);
          const displayChanges = deduplicateStatusChanges(snapshot.changesFromPrevious);
          const previousSnapshot = idx < visibleSnapshots.length - 1 ? visibleSnapshots[idx + 1] : null;
          const currentEvalStatus = snapshot.animalEvalStatus;
          const previousEvalStatus = previousSnapshot?.animalEvalStatus || null;
          const hasEvaluationChange = currentEvalStatus !== null && currentEvalStatus !== previousEvalStatus;

          return (
            <div
              key={idx}
              className="space-y-1"
              data-testid={`snapshot-${stay.stayIndex}-${idx}`}
            >
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: getStayColor(stay.stayIndex) }} />
                <span
                  className="text-sm font-medium"
                  data-testid={`snapshot-date-${stay.stayIndex}-${idx}`}
                >
                  {formatReportDate(snapshot.reportDate, "MMM d, yyyy")}
                </span>
                <Badge
                  variant="outline"
                  className="text-xs"
                  data-testid={`snapshot-location-${stay.stayIndex}-${idx}`}
                >
                  {snapshot.location}
                </Badge>
              </div>

              {showChanges && (
                <>
                  <div
                    className="ml-4 text-xs text-muted-foreground"
                    data-testid={`snapshot-status-summary-${stay.stayIndex}-${idx}`}
                  >
                    {statusSummary}
                  </div>

                  {hasEvaluationChange && (
                    <div
                      className="ml-4 flex items-center gap-2 text-xs"
                      data-testid={`snapshot-evaluation-${stay.stayIndex}-${idx}`}
                    >
                      <span className="text-muted-foreground">Behavior evaluation:</span>
                      <EvalStatusBadge status={currentEvalStatus} className="text-xs" />
                    </div>
                  )}

                  {displayChanges.length > 0 ? (
                    <div
                      className="ml-4 space-y-1"
                      data-testid={`snapshot-changes-${stay.stayIndex}-${idx}`}
                    >
                      {displayChanges.map((change, changeIdx) => {
                        const label = getTimelineChangeLabel(change.field);
                        return (
                          <div
                            key={changeIdx}
                            className="flex items-center gap-2 text-xs"
                            data-testid={`change-${stay.stayIndex}-${idx}-${changeIdx}`}
                          >
                            <Activity className="h-3 w-3 text-warning" />
                            <span className="text-muted-foreground">{label}:</span>
                            <span className="line-through text-muted-foreground">{change.oldValue || "None"}</span>
                            <span>→</span>
                            <span className="font-medium">{change.newValue || "None"}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className="ml-4 text-xs text-muted-foreground italic"
                      data-testid={`snapshot-no-changes-${stay.stayIndex}-${idx}`}
                    >
                      No changes from previous report
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {needsTruncation && !expanded && (
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mx-auto pt-1"
          onClick={() => setExpanded(true)}
          data-testid={`button-view-full-stay-${stay.stayIndex}`}
        >
          <ChevronDown className="h-3.5 w-3.5" />
          <span>View full stay ({hiddenCount} more)</span>
        </button>
      )}

      {needsTruncation && expanded && (
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mx-auto pt-1"
          onClick={() => setExpanded(false)}
          data-testid={`button-collapse-stay-${stay.stayIndex}`}
        >
          <ChevronDown className="h-3.5 w-3.5 rotate-180" />
          <span>Show less</span>
        </button>
      )}
    </div>
  );
}

export function AnimalTimelineCard({
  timeline,
  animalId,
  onOutcomeReasonChange,
  isOutcomeReasonPending = false,
  localOutcomeReasons = {},
}: AnimalTimelineCardProps) {
  const hasStays = timeline && timeline.stays.length > 0;
  const [showChanges, setShowChanges] = useState(false);

  const { data: statusHistory } = useQuery<StatusHistory>({
    queryKey: ["/api/animals", animalId, "status-history"],
    queryFn: async () => {
      const res = await fetch(`/api/animals/${animalId}/status-history`);
      if (!res.ok) throw new Error("Failed to fetch status history");
      return res.json();
    },
    enabled: !!animalId,
  });

  const kennelHistory = statusHistory?.kennelStatusHistory ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-4">
          <CardTitle>Stays</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              id="show-changes"
              checked={showChanges}
              onCheckedChange={setShowChanges}
              data-testid="switch-show-changes"
            />
            <Label htmlFor="show-changes" className="text-sm text-muted-foreground cursor-pointer">
              Show changes
            </Label>
          </div>
        </div>
        <CardDescription>
          {hasStays ? (() => {
            const numAdmissions = new Set(timeline.stays.map(s => s.admissionGroupId ?? 1)).size;
            const stayCount = timeline.stays.length;
            if (numAdmissions < stayCount && numAdmissions === 1) {
              return `Daily snapshots across 1 stay (1 admission, ${stayCount} locations)`;
            }
            if (numAdmissions < stayCount) {
              return `Daily snapshots across ${numAdmissions} ${numAdmissions === 1 ? "stay" : "stays"} (${numAdmissions} admissions)`;
            }
            return `Daily snapshots across ${stayCount} ${stayCount === 1 ? "stay" : "stays"}`;
          })() : "Historical snapshot data"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {hasStays ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {(() => {
              const reversedStays = [...timeline.stays].reverse();
              const numAdmissions = new Set(reversedStays.map(s => s.admissionGroupId ?? 1)).size;
              const hasTransfers = reversedStays.some(s => s.linkedToPriorStay);
              const showAdmissionHeadings = numAdmissions > 1 || hasTransfers;

              // Groups with more than one stay are transfer groups — sub-cards show location only
              const groupSizes = new Map<number, number>();
              for (const s of reversedStays) {
                const gid = s.admissionGroupId ?? 1;
                groupSizes.set(gid, (groupSizes.get(gid) ?? 0) + 1);
              }

              const items: ReactNode[] = [];
              let lastGroupId = -1;

              for (const stay of reversedStays) {
                const groupId = stay.admissionGroupId ?? 1;
                const isNewGroup = groupId !== lastGroupId;
                const inTransferGroup = (groupSizes.get(groupId) ?? 1) > 1;

                if (isNewGroup) {
                  if (lastGroupId !== -1) {
                    items.push(
                      <div
                        key={`divider-group-${groupId}`}
                        className="col-span-full flex items-center gap-3 py-2"
                      >
                        <div className="h-px flex-1 bg-border" />
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                          New Admission
                        </div>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                    );
                  }

                  if (showAdmissionHeadings) {
                    items.push(
                      <div
                        key={`heading-group-${groupId}`}
                        className="col-span-full flex items-center gap-2"
                      >
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-xs font-medium text-muted-foreground">
                          Admission {groupId}
                        </span>
                      </div>
                    );
                  }
                }

                items.push(
                  <StaySection
                    key={stay.stayIndex}
                    stay={stay}
                    showChanges={showChanges}
                    kennelSegments={segmentsForStay(kennelHistory, stay.startDate, stay.endDate)}
                    onOutcomeReasonChange={onOutcomeReasonChange}
                    isOutcomeReasonPending={isOutcomeReasonPending}
                    localOutcomeReasons={localOutcomeReasons}
                    inTransferGroup={inTransferGroup}
                  />
                );

                if (stay.linkedToPriorStay) {
                  const toLabel = LOCATION_LABELS[stay.location ?? ""] ?? stay.location ?? "Unknown";
                  const transferDateStr = stay.startDate
                    ? format(new Date(stay.startDate.split("T")[0] + "T00:00:00"), "MMM d, yyyy")
                    : null;
                  items.push(
                    <div
                      key={`transfer-banner-${stay.stayIndex}`}
                      className="col-span-full flex items-center gap-3"
                    >
                      <div className="h-px flex-1 bg-border" />
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <ArrowRight className="h-3 w-3 shrink-0" />
                        <span>
                          Transferred to {toLabel}
                          {transferDateStr ? ` · ${transferDateStr}` : ""}
                        </span>
                      </div>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                  );
                }

                lastGroupId = groupId;
              }

              return items;
            })()}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No stay data available</p>
            <p className="text-xs mt-2">
              Stay history will appear once the animal is tracked across
              multiple reports
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
